import React from 'react';
import { Badge } from '@/admin/components/ui/badge';
import { Button } from '@/admin/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/admin/components/ui/card';
import {
  createCheckoutSession,
  fetchBillingCredits,
  fetchBillingPlans,
  type BillingCredits,
  type BillingPlan,
} from '@/admin/lib/api';

const creditFormatter = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

const formatPlanName = (planCode: string) => {
  return planCode
    .replace(/_/g, ' ')
    .replace(/\b\w/g, match => match.toUpperCase());
};

const formatPlanType = (type: string) => {
  switch (type.toLowerCase()) {
    case 'subscription':
      return 'Subscription';
    case 'addon':
      return 'Add-on';
    case 'bonus':
      return 'Bonus';
    default:
      return type.replace(/\b\w/g, match => match.toUpperCase());
  }
};

const formatPeriod = (period: string) => {
  switch (period.toLowerCase()) {
    case 'monthly':
      return 'month';
    case 'annual':
      return 'year';
    case 'once':
      return 'one-time';
    default:
      return period;
  }
};

const formatCredits = (credits: number) => {
  if (!Number.isFinite(credits)) return '0';
  return creditFormatter.format(credits);
};

const formatCurrency = (amountCents: number, currency: string) => {
  if (amountCents === 0) return 'Free';
  const normalizedCurrency = currency ? currency.toUpperCase() : 'USD';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: normalizedCurrency,
      maximumFractionDigits: 2,
    }).format(amountCents / 100);
  } catch {
    return `$${(amountCents / 100).toFixed(2)}`;
  }
};

const formatStatus = (status?: string | null) => {
  if (!status) return 'No subscription';
  return status
    .replace(/_/g, ' ')
    .replace(/\b\w/g, match => match.toUpperCase());
};

const getCheckoutMode = (plan: BillingPlan) => {
  switch (plan.type.toLowerCase()) {
    case 'subscription':
      return 'subscription';
    case 'addon':
      return 'payment';
    default:
      return null;
  }
};

export const PlansPage: React.FC = () => {
  const [plans, setPlans] = React.useState<BillingPlan[]>([]);
  const [credits, setCredits] = React.useState<BillingCredits | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [checkoutPlanCode, setCheckoutPlanCode] = React.useState<string | null>(
    null
  );

  const loadData = React.useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [plansResponse, creditsResponse] = await Promise.all([
        fetchBillingPlans(),
        fetchBillingCredits(),
      ]);
      setPlans(plansResponse.plans);
      setCredits(creditsResponse);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load plans');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCheckout = async (plan: BillingPlan) => {
    const mode = getCheckoutMode(plan);
    if (!mode) {
      setError('Checkout is not available for this plan.');
      return;
    }

    setCheckoutPlanCode(plan.plan_code);
    setError(null);

    try {
      const returnUrl = new URL('/plans', window.location.origin).toString();
      const response = await createCheckoutSession({
        lookup_key: plan.plan_code,
        mode,
        success_url: returnUrl,
        cancel_url: returnUrl,
      });
      window.location.assign(response.checkout_url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Checkout failed');
    } finally {
      setCheckoutPlanCode(null);
    }
  };

  const currentPlanCode = credits?.plan_code ?? null;
  const currentPlan = plans.find(plan => plan.plan_code === currentPlanCode);
  const currentPlanLabel = currentPlan
    ? formatPlanName(currentPlan.plan_code)
    : currentPlanCode
    ? formatPlanName(currentPlanCode)
    : 'No active plan';
  const nextResetLabel = credits?.next_reset_at
    ? new Date(credits.next_reset_at).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  return (
    <div className="flex flex-1 flex-col gap-6 overflow-auto py-4 md:py-6">
      <div className="px-4 lg:px-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Plans</h1>
            <p className="text-sm text-muted-foreground">
              Review available plans and start a checkout session.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={loadData}
            disabled={loading}
          >
            Refresh
          </Button>
        </div>
      </div>

      {error ? (
        <div className="px-4 lg:px-6">
          <Card className="border-destructive/40">
            <CardHeader>
              <CardTitle className="text-base">Unable to load billing</CardTitle>
              <CardDescription>{error}</CardDescription>
            </CardHeader>
          </Card>
        </div>
      ) : null}

      <div className="grid gap-6 px-4 lg:px-6 lg:grid-cols-[1.1fr_1.3fr]">
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <CardTitle>Current Plan</CardTitle>
              <Badge variant="secondary">
                {formatStatus(credits?.sub_status)}
              </Badge>
            </div>
            <CardDescription>
              Active plan details for the signed-in user.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <div className="text-lg font-semibold">{currentPlanLabel}</div>
            <div className="text-sm text-muted-foreground">
              Available credits:{' '}
              <span className="font-medium text-foreground">
                {formatCredits(credits?.available_credits ?? 0)}
              </span>
            </div>
            {nextResetLabel ? (
              <div className="text-sm text-muted-foreground">
                Next reset on {nextResetLabel}.
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>How Billing Works</CardTitle>
            <CardDescription>
              Subscriptions renew automatically, and add-ons are one-time top-ups.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 text-sm text-muted-foreground">
            <div>
              Plans are grouped by type. Subscription plans refresh credits on
              their cadence, while add-ons deliver credits immediately.
            </div>
            <div>
              Checkout launches Stripe in a new session, using the plan code as
              the lookup key.
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="px-4 lg:px-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">Available Plans</h2>
            <p className="text-sm text-muted-foreground">
              {plans.length} plan{plans.length === 1 ? '' : 's'} available
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-4 px-4 lg:px-6 md:grid-cols-2 xl:grid-cols-3">
        {loading ? (
          Array.from({ length: 3 }).map((_, index) => (
            <Card key={`plan-skeleton-${index}`} className="animate-pulse">
              <CardHeader>
                <div className="h-4 w-24 rounded bg-muted" />
                <div className="h-6 w-40 rounded bg-muted" />
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="h-8 w-32 rounded bg-muted" />
                <div className="h-4 w-full rounded bg-muted" />
              </CardContent>
            </Card>
          ))
        ) : plans.length === 0 ? (
          <Card className="md:col-span-2 xl:col-span-3">
            <CardHeader>
              <CardTitle>No plans available</CardTitle>
              <CardDescription>
                Billing plans have not been configured yet.
              </CardDescription>
            </CardHeader>
          </Card>
        ) : (
          plans.map(plan => {
            const isCurrent = plan.plan_code === currentPlanCode;
            const mode = getCheckoutMode(plan);

            return (
              <Card key={plan.plan_code} className={isCurrent ? 'border-primary/40' : ''}>
                <CardHeader>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <CardTitle className="text-base">
                        {formatPlanName(plan.plan_code)}
                      </CardTitle>
                      <CardDescription>
                        {formatPlanType(plan.type)}
                      </CardDescription>
                    </div>
                    {isCurrent ? <Badge>Current</Badge> : null}
                  </div>
                </CardHeader>
                <CardContent className="flex flex-col gap-4">
                  <div>
                    <div className="text-2xl font-semibold">
                      {formatCurrency(plan.amount_cents, plan.currency)}
                      <span className="text-sm font-normal text-muted-foreground">
                        {' '}
                        / {formatPeriod(plan.period)}
                      </span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {plan.display_copy ||
                        `${formatCredits(plan.credits_grant)} credits per ${formatPeriod(
                          plan.period
                        )}.`}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                    <Badge variant="outline">
                      {formatCredits(plan.credits_grant)} credits
                    </Badge>
                    <Badge variant="outline">
                      {plan.currency.toUpperCase()}
                    </Badge>
                    <Badge variant="outline">
                      {formatPeriod(plan.period)}
                    </Badge>
                  </div>

                  <Button
                    onClick={() => handleCheckout(plan)}
                    disabled={!mode || checkoutPlanCode === plan.plan_code}
                  >
                    {checkoutPlanCode === plan.plan_code
                      ? 'Opening checkout...'
                      : 'Checkout'}
                  </Button>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};
